import { Box } from "@mui/material";
import {
  VALID_FORM_CODES,
  VALID_MANAGEMENT_METHOD_CODES,
  VALID_SOURCE_CODES,
} from "encamp-shared/src/waste/validCodes";
import { WasteClassification, WasteKind } from "generated-graphql/graphql";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";

import {
  prettyPrintWasteClassification,
  prettyPrintWasteProgram,
  prettyPrintWasteStreamLabel,
} from "./Facilities/Facility/prettyPrintWasteClassification";
import { WasteClassificationChip } from "./WasteClassificationChip";
import { WasteStreamAmountCell, WasteStreamAmountHeader } from "./WasteStreamAmountCell";
import {
  WS_DISCREPANCY_FIELD_LABELS,
  WasteStreamDiscrepancyRow,
} from "./wasteStreamDiscrepancyIndicator";
import { WasteStreamFieldWarning } from "./WasteStreamFieldWarning";

/**
 * The waste-stream fields shared by the tenant and facility grids' code/amount
 * columns. Kept minimal so both generated row types satisfy it.
 */
export interface WasteStreamCodeColumnRow {
  federalWasteCodes?: string[] | null;
  stateWasteCodes?: string[] | null;
  classification?: WasteClassification | null;
  wasteKind?: WasteKind | null;
  formCode?: string | null;
  sourceCode?: string | null;
  managementMethodCodes: string[];
  vendorAmount?: number | null;
  eManifestAmount?: number | null;
  eManifestAmountWithOverride?: number | null;
  documentAmount?: number | null;
  effectiveAmount?: number | null;
  discrepancies: ReadonlyArray<WasteStreamDiscrepancyRow>;
}

/**
 * The hazard class and the management program as separate columns, hidden until
 * a user turns them on from Manage Columns.
 *
 * The combined Classification column is what most people want; these are for
 * scanning or copying one axis at a time. Neither sorts nor filters: the server
 * does both on the stored classification, which the combined column already
 * exposes, so a control here would order by one string and show another.
 */
/**
 * Hides the single-axis columns until a user asks for them, so the default grid
 * is the combined Classification column alone.
 *
 * Shared by both grids rather than written out at each call site: the two would
 * otherwise drift, and a stream would offer different columns depending on
 * which grid you opened it from.
 */
export const SINGLE_AXIS_COLUMNS_HIDDEN: Record<string, boolean> = {
  hazardClass: false,
  wasteKind: false,
};

function singleAxisClassificationColumns<
  Row extends WasteStreamCodeColumnRow,
>(): OmnisearchGridColDef<Row>[] {
  return [
    {
      field: "hazardClass",
      headerName: "Hazard Class",
      flex: 0.4,
      filterable: false,
      sortable: false,
      valueGetter: (_, row) => prettyPrintWasteClassification(row.classification),
      headerClassName: "wrapHeader",
    },
    {
      field: "wasteKind",
      headerName: "Management Program",
      flex: 0.4,
      filterable: false,
      sortable: false,
      valueGetter: (_, row) => prettyPrintWasteProgram(row.wasteKind),
      headerClassName: "wrapHeader",
    },
  ];
}

/**
 * The waste-code / classification / amount columns shared verbatim by the tenant
 * (`TenantWasteStreams`) and facility (`WasteStreamsDataGrid`) grids: Federal /
 * State Waste Code, Classification, Form / Source / Management Method Code, and
 * Amount — each with its per-cell discrepancy indicator. Defined once here so an
 * edit lands in both grids instead of drifting between two hand-kept copies.
 *
 * `showDiscrepancies` gates the (!) indicators (the flag is staff/add-on driven
 * in each grid). `isStaff` scopes which discrepancy statuses each indicator
 * treats as open (staff see PENDING + UNRESOLVED, non-staff see PENDING only).
 * Grid-specific columns (Facility, Waste Stream, Registration, Issues) stay in
 * the grids since they diverge.
 */
export function buildWasteStreamCodeColumns<Row extends WasteStreamCodeColumnRow>(
  showDiscrepancies: boolean,
  isStaff: boolean
): OmnisearchGridColDef<Row>[] {
  return [
    {
      field: "federalWasteCodes",
      headerName: "Federal Waste Code",
      flex: 0.5,
      sortable: true,
      valueGetter: (_, row) => (row.federalWasteCodes || []).join(", "),
      renderCell: ({ value, row }) => (
        <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
          <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            {value}
          </Box>
          {showDiscrepancies && (
            <WasteStreamFieldWarning
              discrepancies={row.discrepancies}
              field="federalWasteCodes"
              fieldLabel={WS_DISCREPANCY_FIELD_LABELS.federalWasteCodes}
              inline
              isStaff={isStaff}
            />
          )}
        </Box>
      ),
      headerClassName: "wrapHeader",
    },
    {
      field: "stateWasteCodes",
      headerName: "State Waste Code",
      flex: 0.5,
      sortable: true,
      valueGetter: (_, row) => (row.stateWasteCodes || []).join(", "),
      renderCell: ({ value, row }) => (
        <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
          <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            {value}
          </Box>
          {showDiscrepancies && (
            <WasteStreamFieldWarning
              discrepancies={row.discrepancies}
              field="stateWasteCodes"
              fieldLabel={WS_DISCREPANCY_FIELD_LABELS.stateWasteCodes}
              includeFieldIssues
              inline
              isStaff={isStaff}
            />
          )}
        </Box>
      ),
      headerClassName: "wrapHeader",
    },
    {
      field: "classification",
      headerName: "Classification",
      flex: 0.5,
      filterable: false,
      sortable: true,
      // valueFormatter keeps a readable string for clipboard copy and screen
      // readers; the chip is the visual. Both carry the management program so
      // the two agree. The downloaded workbook is built separately in
      // export-data: the raw classification enum (so bulk-edit import can map
      // it back) plus a separate Management Program column.
      valueFormatter: (_, row) => prettyPrintWasteStreamLabel(row.classification, row.wasteKind),
      renderCell: ({ row }) => (
        <WasteClassificationChip classification={row.classification} wasteKind={row.wasteKind} />
      ),
      headerClassName: "wrapHeader",
    },
    ...singleAxisClassificationColumns<Row>(),
    {
      field: "formCode",
      headerName: "Form Code",
      filterKeyType: "enumAutocomplete",
      enumValues: [...VALID_FORM_CODES],
      flex: 0.3,
      sortable: true,
      renderCell: ({ value, row }) => (
        <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
          <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            {value}
          </Box>
          {showDiscrepancies && (
            <WasteStreamFieldWarning
              discrepancies={row.discrepancies}
              field="formCode"
              fieldLabel={WS_DISCREPANCY_FIELD_LABELS.formCode}
              inline
              isStaff={isStaff}
            />
          )}
        </Box>
      ),
      headerClassName: "wrapHeader",
    },
    {
      field: "sourceCode",
      headerName: "Source Code",
      filterKeyType: "enumAutocomplete",
      enumValues: [...VALID_SOURCE_CODES],
      flex: 0.3,
      sortable: true,
      renderCell: ({ value, row }) => (
        <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
          <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            {value}
          </Box>
          {showDiscrepancies && (
            <WasteStreamFieldWarning
              discrepancies={row.discrepancies}
              field="sourceCode"
              fieldLabel={WS_DISCREPANCY_FIELD_LABELS.sourceCode}
              inline
              isStaff={isStaff}
            />
          )}
        </Box>
      ),
      headerClassName: "wrapHeader",
    },
    {
      field: "managementMethodCodes",
      headerName: "Management Method Code",
      filterKeyType: "enumAutocomplete",
      enumValues: [...VALID_MANAGEMENT_METHOD_CODES],
      flex: 0.5,
      sortable: true,
      valueGetter: (_, row) => row.managementMethodCodes.join(", "),
      renderCell: ({ value, row }) => (
        <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
          <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            {value}
          </Box>
          {showDiscrepancies && (
            <WasteStreamFieldWarning
              discrepancies={row.discrepancies}
              field="managementMethodCode"
              fieldLabel={WS_DISCREPANCY_FIELD_LABELS.managementMethodCode}
              inline
              isStaff={isStaff}
            />
          )}
        </Box>
      ),
      headerClassName: "wrapHeader",
    },
    {
      // Canonical effective amount with a per-source tooltip. Not filterable:
      // effectiveAmount is a server aggregate.
      field: "effectiveAmount",
      headerName: "Amount",
      flex: 0.4,
      filterable: false,
      sortable: true,
      headerClassName: "wrapHeader",
      renderHeader: () => <WasteStreamAmountHeader />,
      // WasteStreamAmountCell surfaces the per-source amounts on hover when the
      // raw values differ. The (!) is driven off persisted quantity-discrepancy
      // rows only — it flags the case the amount cell can't (a flagged
      // discrepancy whose consolidated amounts happen to match), without
      // doubling up on the raw-disagreement signal.
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
          <WasteStreamAmountCell
            amount={row.effectiveAmount}
            vendorAmount={row.vendorAmount}
            eManifestAmount={row.eManifestAmount}
            eManifestAmountWithOverride={row.eManifestAmountWithOverride}
            documentAmount={row.documentAmount}
          />
          {showDiscrepancies && (
            <WasteStreamFieldWarning
              discrepancies={row.discrepancies}
              field="quantity"
              fieldLabel={WS_DISCREPANCY_FIELD_LABELS.quantity}
              inline
              isStaff={isStaff}
            />
          )}
        </Box>
      ),
    },
  ];
}
